import React, { useCallback, useMemo, useState } from 'react';
import { OrderMetadata } from './types';
import { OriginalInboundAttachments } from './original-inbound-attachments';
import { OriginalInboundMessageHeader } from './original-inbound-message-header';
import { OriginalInboundText } from './original-inbound-text';
import { OriginalInboundVoicemail } from './original-inbound-voicemail';
import { SCREEN_PADDING_SMALL } from '@/styles';
import { StyleSheet, View } from 'react-native';

interface Props {
  isFirstOrder?: boolean;
  isSummaryView?: boolean;
  messageHeader?: React.ReactNode;
  onSelect?: (inputUUID: string) => void;
  order: OrderMetadata;
  selectedInputUUID?: string;
  showHeaderMetadata?: boolean;
  showTranslation?: boolean;
}

export const OriginalInboundMessage: React.FC<Props> = ({
  isFirstOrder,
  showHeaderMetadata,
  isSummaryView,
  onSelect,
  order,
  selectedInputUUID,
  showTranslation,
  messageHeader,
}) => {
  const [hasEmlError, setHasEmlError] = useState(false);
  const [hasHtmlWithNoContent, setHasHtmlWithNoContent] = useState(false);

  const shouldShowOriginalInboundText = useMemo(() => {
    if (!order.searchText) {
      return false;
    }

    const hasEmlAttachment =
      order.attachments?.some((attachment) => attachment.type === 'EML') ??
      false;

    return !hasEmlAttachment || hasEmlError || hasHtmlWithNoContent;
  }, [order.searchText, order.attachments, hasEmlError, hasHtmlWithNoContent]);

  const onEmlError = useCallback(() => {
    setHasEmlError(true);
  }, []);

  const onHtmlHasNoContent = useCallback(() => {
    setHasHtmlWithNoContent(true);
  }, []);

  return (
    <View style={styles.section}>
      <OriginalInboundMessageHeader
        isFirstOrder={isFirstOrder}
        isSummaryView={isSummaryView}
        order={order}
        showHeaderMetadata={showHeaderMetadata}
        showTranslation={showTranslation}
      />
      {order.voicemailRecordingUrl ? (
        <OriginalInboundVoicemail
          messageHeader={messageHeader}
          onSelect={onSelect}
          order={order}
          selectedInputUUID={selectedInputUUID}
          showTranslation={showTranslation}
        />
      ) : order.attachments ? (
        <>
          {messageHeader}
          {shouldShowOriginalInboundText && (
            <OriginalInboundText
              onSelect={onSelect}
              order={order}
              selectedInputUUID={selectedInputUUID}
              showTranslation={showTranslation}
            />
          )}
          <OriginalInboundAttachments
            onEmlError={onEmlError}
            onHtmlHasNoContent={onHtmlHasNoContent}
            onSelect={onSelect}
            order={order}
            selectedInputUUID={selectedInputUUID}
            showTranslation={showTranslation}
          />
        </>
      ) : (
        <>
          {messageHeader}
          <OriginalInboundText
            onSelect={onSelect}
            order={order}
            selectedInputUUID={selectedInputUUID}
            showTranslation={showTranslation}
          />
        </>
      )}
    </View>
  );
};

export const styles = StyleSheet.create({
  section: {
    gap: SCREEN_PADDING_SMALL,
    paddingVertical: SCREEN_PADDING_SMALL,
  },
});
